<template>
  <div class="flex min-h-0 flex-1 flex-col gap-6">
    <!-- Toolbar -->
    <div class="flex items-center gap-2">
      <div class="relative min-w-0 max-w-sm flex-1">
        <Search
          class="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          v-model="localQuery"
          :placeholder="$t('Поиск по Face ID')"
          class="h-9 pl-9 text-sm"
          @input="onSearch"
        />
      </div>
      <div class="flex-1" />
      <Button variant="default" size="sm" @click="$emit('create')">
        <svg-icon name="huge-plus" class="h-3 w-3" />
        {{ $t("Создать") }}
      </Button>
      <BaseTabs
        v-if="viewTabs"
        inline
        :tabs="viewTabs"
        :active-tab="activeView"
        @change:tab="$emit('update:activeView', $event)"
      />
    </div>

    <!-- Table -->
    <div class="table-area flex min-h-0 flex-1 flex-col">
      <div class="table-scroll min-h-0 flex-1 overflow-auto">
        <Table :loading="loading" :scroll-reset-key="page">
          <TableHeader>
            <TableRow>
              <TableHead>{{ $t("Пользователь") }}</TableHead>
              <TableHead>{{ $t("Фото") }}</TableHead>
              <TableHead>{{ $t("Дата регистрации") }}</TableHead>
              <TableHead>{{ $t("Последний вход") }}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableEmpty v-if="!loading && !users.length" :colspan="4">
              {{ $t("Ничего не найдено") }}
            </TableEmpty>
            <TableRow
              v-for="row in users"
              :key="row.id"
              class="cursor-pointer hover:bg-muted/50"
              @click="$emit('open', row)"
            >
              <TableCell>
                <div class="flex items-center gap-2.5">
                  <UserAvatar
                    :identifier="row.identifier"
                    :seed="row.id"
                    :name="row.name"
                    :size="32"
                  />
                  <div class="min-w-0">
                    <div class="truncate text-sm font-medium text-foreground">
                      {{ row.name || "—" }}
                    </div>
                    <div class="truncate text-muted-foreground">
                      {{ row.identifier || row.email || row.id }}
                    </div>
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <FaceThumbsCell
                  :count="row.faces_count || 0"
                  :seed="String(row.id)"
                  :gender="row.gender"
                />
              </TableCell>
              <TableCell class="text-muted-foreground">
                {{ formatDate(row.enrollment_date) }}
              </TableCell>
              <TableCell class="text-muted-foreground">
                {{ row.last_seen ? formatDateTime(row.last_seen) : "—" }}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>

      <TableFooterWrapper
        :total="total"
        :page-size="pageSize"
        :current-page="page"
        :page-sizes="[10, 20, 50]"
        @update:page-size="$emit('update:pageSize', $event)"
        @update:current-page="$emit('update:page', $event)"
      >
        <template #pagination>
          <Pagination
            v-if="total > pageSize"
            :total="total"
            :items-per-page="pageSize"
            :page="page"
            :sibling-count="1"
            show-edges
            @update:page="$emit('update:page', $event)"
          >
            <PaginationContent v-slot="{ items }">
              <PaginationPrevious />
              <template v-for="(item, index) in items" :key="index">
                <PaginationItem
                  v-if="item.type === 'page'"
                  :value="item.value"
                  :is-active="item.value === page"
                />
                <PaginationEllipsis v-else />
              </template>
              <PaginationNext />
            </PaginationContent>
          </Pagination>
        </template>
      </TableFooterWrapper>
    </div>
  </div>
</template>

<script>
import { Search } from "lucide-vue-next";
import { Input } from "@/shared/ui/input";
import Button from "@/shared/ui/button/Button.vue";
import BaseTabs from "@/shared/ui/custom/tabs/BaseTabs.vue";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/table";
import TableFooterWrapper from "@/shared/ui/custom/components/TableFooterWrapper.vue";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/shared/ui/pagination";
import UserAvatar from "./UserAvatar.vue";
import FaceThumbsCell from "./FaceThumbsCell.vue";
import { formatDate, formatDateTime } from "./utils";

export default {
  name: "FaceIdUsersTable",
  components: {
    Search,
    Input,
    Button,
    BaseTabs,
    Table,
    TableBody,
    TableCell,
    TableEmpty,
    TableHead,
    TableHeader,
    TableRow,
    TableFooterWrapper,
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationNext,
    PaginationPrevious,
    UserAvatar,
    FaceThumbsCell,
  },
  props: {
    users: { type: Array, default: () => [] },
    total: { type: Number, default: 0 },
    loading: { type: Boolean, default: false },
    query: { type: String, default: "" },
    page: { type: Number, default: 1 },
    pageSize: { type: Number, default: 10 },
    viewTabs: { type: Object, default: null },
    activeView: { type: String, default: "users" },
  },
  emits: [
    "open",
    "create",
    "update:query",
    "update:page",
    "update:pageSize",
    "update:activeView",
  ],
  data() {
    return {
      localQueryValue: this.query,
      searchTimer: null,
    };
  },
  computed: {
    localQuery: {
      get() {
        return this.localQueryValue;
      },
      set(v) {
        this.localQueryValue = v;
      },
    },
  },
  watch: {
    query(value) {
      this.localQueryValue = value;
    },
  },
  methods: {
    formatDate,
    formatDateTime,
    onSearch() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.$emit("update:query", this.localQueryValue);
      }, 300);
    },
  },
};
</script>
